import { FC, useRef, useState, useEffect } from 'react'
import { Typography, Tooltip } from '@mui/material'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { closeModal, modalPropsSelector } from 'store/modals/modalSlice'
import { getIntegrationLogo } from 'lib/utils'
import axios from 'lib/axios'
import { ManifestIntegration } from 'models'
import theme from 'theme'
import { formatCalculationValue } from '../../../../pages/library/utils'
import { CalculationDetailsWrapper } from './calculationDetails.styles'

const CalculationDetails: FC = () => {
  const dispatch = useAppDispatch()
  const modalProps = useAppSelector(modalPropsSelector)
  const data = modalProps?.props?.calculationDetailsData
  const lastColRef = useRef<HTMLTableCellElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [scoreLeft, setScoreLeft] = useState<number | null>(null)

  const cpis = data?.cpis || []
  const score = data?.score ?? 0
  const lastUpdate = data?.lastUpdate || ''

  // Some migrated data sources have their `display_name` set to the instance
  // name instead of the integration name, so resolve the clean integration
  // display_name from the supported-integrations manifest keyed by `ds.name`.
  const [integrationNameMap, setIntegrationNameMap] = useState<Record<string, string>>({})

  useEffect(() => {
    let cancelled = false
    axios
      .get('/api/v3/integrations/supported')
      .then((res) => {
        if (cancelled || res.status !== 200) return
        const integrations: ManifestIntegration[] = res.data.integrations || []
        const map: Record<string, string> = {}
        integrations.forEach((integration) => {
          if (integration.name && integration.display_name) {
            map[integration.name] = integration.display_name
          }
        })
        setIntegrationNameMap(map)
      })
      .catch((err) => console.error('Error fetching supported integrations:', err))
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (lastColRef.current && wrapperRef.current) {
      const colRect = lastColRef.current.getBoundingClientRect()
      const wrapperRect = wrapperRef.current.getBoundingClientRect()
      setScoreLeft(colRect.left - wrapperRect.left)
    }
  }, [cpis])

  const handleClose = () => {
    dispatch(closeModal())
  }

  return (
    <CalculationDetailsWrapper ref={wrapperRef}>
      <div className="modal-header">
        <Typography className="header-title">Calculation details</Typography>
        <svg
          className="close-button"
          onClick={handleClose}
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14 26L26 14M14 14L26 26"
            stroke="#E9E0E4"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <div className="content-header">
        <span>
          Number of rows: <span className="highlight">{cpis.length}</span>
        </span>
        <span>
          Last Update: <span className="highlight">{lastUpdate}</span>
        </span>
      </div>

      <div className="modal-content">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>CPI</th>
                <th>Source</th>
                <th>SLA</th>
                <th>Most Recent Finding</th>
                <th>Weight</th>
                <th ref={lastColRef}>Performance Score</th>
              </tr>
            </thead>
            <tbody>
              {cpis.map((cpi, index) => (
                <tr key={cpi.cpiName || index}>
                  <td>
                    <div className="cpi-cell">
                      <div className="cpi-chip">
                        <span>{cpi.cpiDisplayName}</span>
                      </div>
                      <span className="cpi-name">{cpi.cpiTitle}</span>
                    </div>
                  </td>
                  <td>
                    <div className="source-logos">
                      {cpi.dataSources?.map((ds, dsIndex) => (
                        <Tooltip
                          key={dsIndex}
                          title={
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <img
                                src={getIntegrationLogo(ds.name, 'circle')}
                                alt=""
                                style={{ width: 16, height: 16, borderRadius: '50%' }}
                              />
                              {integrationNameMap[ds.name] || ds.display_name}
                            </span>
                          }
                          placement="top"
                          PopperProps={{ style: { zIndex: 10003 } }}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                padding: '12px',
                                fontFamily: 'Quicksand, sans-serif',
                                fontSize: '12px',
                                lineHeight: '140%',
                                fontWeight: 600,
                                color: theme.colors.menuItemText,
                                borderRadius: '12px',
                                boxShadow: '0px 4px 8px 0px rgba(30, 26, 29, 0.20)',
                                background: theme.colors.borderMedium
                              }
                            }
                          }}
                        >
                          <img
                            className="source-logo"
                            src={getIntegrationLogo(ds.name, 'circle')}
                            alt={ds.display_name}
                          />
                        </Tooltip>
                      ))}
                    </div>
                  </td>
                  <td>{formatCalculationValue(cpi.sla, cpi.unit, cpi.tooltipUnit)}</td>
                  <td>
                    {cpi.recentFinding
                      ? formatCalculationValue(cpi.recentFinding, '')
                      : formatCalculationValue(cpi.value, cpi.unit, cpi.tooltipUnit)}
                  </td>
                  <td>
                    {cpi.weight != null
                      ? `${Number.isInteger(cpi.weight) ? cpi.weight : parseFloat(cpi.weight.toFixed(1))}%`
                      : '-'}
                  </td>
                  <td>{cpi.score != null ? `${Math.round(cpi.score)}%` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="modal-footer">
        <Typography className="result-label">Result Score:</Typography>
        {scoreLeft != null && (
          <Typography className="result-value" style={{ position: 'absolute', left: scoreLeft + 8 }}>
            {score}%
          </Typography>
        )}
      </div>
    </CalculationDetailsWrapper>
  )
}

export default CalculationDetails
